import { useState, useCallback, useEffect, useRef } from 'react';
import threadService from '../services/threadService';
import { useAuth } from '../context/AuthContext';

export interface Thread {
  id: string;
  title: string;
  [key: string]: any;
}

export interface UseThreadManagementResult {
  threads: Thread[];
  setThreads: (threads: Thread[]) => void;
  currentThread: Thread | null;
  setCurrentThread: (thread: Thread | null) => void;
  isThreadListOpen: boolean;
  setIsThreadListOpen: (open: boolean) => void;
  selectThread: (thread: Thread | null, options?: { targetMessageId?: string }) => void;
  clearTargetMessage: () => void;
  deleteThread: (threadId: string) => Promise<void>;
  renameThread: (threadId: string, newTitle: string) => Promise<void>;
  applyThreadTitle: (threadId: string, newTitle: string) => void;
  createThread: (title: string) => Promise<Thread | null>;
  loadThreads: (reset?: boolean) => Promise<void>;
  loadMoreThreads: () => Promise<void>;
  hasMore: boolean;
  isLoadingMore: boolean;
  isLoading: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  isSearching: boolean;
  searchResults: Thread[];
  agentFilter: string | null;
  setAgentFilter: (rolId: string | null) => void;
}

const PAGE_SIZE = 30;

export const useThreadManagement = (): UseThreadManagementResult => {
  // 🔐 SECURITY: Track user changes to clear state on logout/user switch
  const { user } = useAuth();
  const userId = user?.id;
  const prevUserIdRef = useRef<string | null | undefined>(undefined); // undefined = initial state

  const [threads, setThreads] = useState<Thread[]>([]);
  const [currentThread, setCurrentThread] = useState<Thread | null>(null);
  const [isThreadListOpen, setIsThreadListOpen] = useState(false);

  // Estados para paginación
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const currentPageRef = useRef(0); // ✅ FIX: Use ref to prevent infinite loop in useEffect dependencies
  // ✅ FIX: Synchronous guard against IntersectionObserver cascade.
  // React state updates are async (batched), so isLoadingMore can be stale
  // in the observer closure. This ref is set synchronously BEFORE any await,
  // guaranteeing that concurrent observer fires are blocked immediately.
  const isLoadingMoreRef = useRef(false);

  // Estados para búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Thread[]>([]);
  const [allThreads, setAllThreads] = useState<Thread[]>([]); // Backup de todos los threads
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Estados para filtro por agente
  const [agentFilter, setAgentFilterState] = useState<string | null>(null);
  const [agentFilterResults, setAgentFilterResults] = useState<Thread[]>([]);

  // Cargar threads desde el servicio con soporte para paginación
  const loadThreads = useCallback(async (reset: boolean = false) => {
    try {
      setIsLoading(true);
      const offset = reset ? 0 : currentPageRef.current * PAGE_SIZE;

      const result = await threadService.getThreads({
        limit: PAGE_SIZE,
        offset
      });

      // ✅ FIX: Update currentPageRef BEFORE setThreads to prevent race condition
      // with IntersectionObserver triggering loadMoreThreads with stale offset
      if (reset) {
        currentPageRef.current = 1; // ✅ Set BEFORE setThreads
        setThreads(result.threads);
      } else {
        currentPageRef.current = 1; // ✅ Set BEFORE setThreads
        setThreads(result.threads);
      }

      setHasMore(result.hasMore);
      console.log(`[useThreadManagement] Cargados ${result.threads.length} threads. HasMore: ${result.hasMore}, Total: ${result.total}`);
    } catch (error) {
      console.error('[useThreadManagement] Error al cargar threads:', error);
      setThreads([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, []); // ✅ FIX: Empty deps - no longer depends on currentPage

  // 🔐 SECURITY: Clear all state when user changes (logout or user switch)
  // This prevents data leakage between user sessions
  useEffect(() => {
    // Skip initial render (prevUserIdRef starts as undefined)
    if (prevUserIdRef.current === undefined) {
      prevUserIdRef.current = userId;
      return;
    }

    // Detect user change (including logout → null)
    if (prevUserIdRef.current !== userId) {
      console.log('[useThreadManagement] 🔐 User changed, clearing all state', {
        from: prevUserIdRef.current?.substring(0, 8) || 'null',
        to: userId?.substring(0, 8) || 'null'
      });

      // Clear ALL thread state to prevent data leakage
      setThreads([]);
      setAllThreads([]);
      setCurrentThread(null);
      setSearchTerm('');
      setSearchResults([]);
      currentPageRef.current = 0;
      isLoadingMoreRef.current = false;
      setHasMore(true);

      // Update ref to new user
      prevUserIdRef.current = userId;

      // If new user logged in, trigger fresh load
      // Note: loadThreads will use the new user's token from AuthContext
      if (userId) {
        console.log('[useThreadManagement] 🔄 Loading threads for new user');
        loadThreads(true);
      }
    }
  }, [userId, loadThreads]);

  // Cargar más threads para infinite scroll
  const loadMoreThreads = useCallback(async () => {
    // ✅ FIX: Synchronous ref check — immune to stale closures in IntersectionObserver
    if (isLoadingMoreRef.current || !hasMore || isLoading) {
      return;
    }
    isLoadingMoreRef.current = true; // Block immediately (sync, before any await)

    try {
      setIsLoadingMore(true);
      const offset = currentPageRef.current * PAGE_SIZE;

      console.log(`[useThreadManagement] Cargando más threads. Offset: ${offset}, Page: ${currentPageRef.current}`);

      const result = await threadService.getThreads({
        limit: PAGE_SIZE,
        offset
      });

      // ✅ FIX: Deduplicate to prevent intermittent duplication bug
      // Race condition: IntersectionObserver can fire multiple times before isLoadingMore state updates
      // Pattern: Same as useN8NDaemonHandler.js:24-27 and useMessages.ts:371-376
      setThreads(prev => {
        const existingIds = new Set(prev.map(t => t.id));
        const newThreads = result.threads.filter(t => {
          if (existingIds.has(t.id)) {
            console.warn('[useThreadManagement] ⚠️ Duplicate thread filtered in loadMoreThreads:', t.id);
            return false;
          }
          return true;
        });
        return [...prev, ...newThreads];
      });
      currentPageRef.current = currentPageRef.current + 1;
      setHasMore(result.hasMore);

      console.log(`[useThreadManagement] Cargados ${result.threads.length} threads adicionales. HasMore: ${result.hasMore}`);
    } catch (error) {
      console.error('[useThreadManagement] Error al cargar más threads:', error);
      setHasMore(false);
    } finally {
      isLoadingMoreRef.current = false; // Unblock (sync)
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoading]); // ✅ Removed isLoadingMore dep — ref is the guard now

  // Seleccionar un thread.
  // #212: optional targetMessageId lets a caller (e.g. plan-execution deep-link)
  // request a scroll-to-message; it is stamped on the thread object so Chat can
  // consume it once after selection.
  const selectThread = useCallback((thread: Thread | null, options?: { targetMessageId?: string }) => {
    const targetMessageId = options?.targetMessageId;
    setCurrentThread(
      thread && targetMessageId ? { ...thread, targetMessageId } : thread
    );
    setIsThreadListOpen(false);
  }, []);

  // #212: el deep-link se consume una sola vez. Si no se limpiara, al volver a
  // entrar al chat (remount) el scroll saltaría otra vez al mensaje disparador
  // en lugar de al último mensaje.
  const clearTargetMessage = useCallback(() => {
    setCurrentThread(prev =>
      prev?.targetMessageId ? { ...prev, targetMessageId: undefined } : prev
    );
  }, []);

  // Crear un nuevo thread
  const createThread = useCallback(async (title: string) => {
    try {
      const newThread = await threadService.createThread(title);
      if (newThread && newThread.id) {
        // Insertar el nuevo thread al inicio (ordenado alfabéticamente después)
        // ✅ FIX: Check for duplicates before adding (defensive, same pattern as useN8NDaemonHandler.js:25)
        setThreads(prev => {
          if (prev.some(t => t.id === newThread.id)) {
            console.warn('[useThreadManagement] ⚠️ Thread already exists in createThread:', newThread.id);
            return prev;
          }
          const updated = [newThread, ...prev];
          // Re-ordenar alfabéticamente
          return updated.sort((a, b) => a.title.localeCompare(b.title));
        });
        setCurrentThread(newThread);
        return newThread;
      }
      return null;
    } catch (error) {
      console.error('[useThreadManagement] Error al crear thread:', error);
      return null;
    }
  }, []);

  // Borrar un thread
  const deleteThread = useCallback(async (threadId: string) => {
    try {
      await threadService.deleteThread(threadId);
      // Podar el thread de TODAS las cachés, no solo de `threads`: la lista
      // visible puede venir de searchResults/agentFilterResults/allThreads
      // (ver getVisibleThreads). Si solo podáramos `threads`, el chat borrado
      // seguiría visible en vistas con búsqueda o filtro de agente activos
      // hasta refrescar (issue #102).
      const remove = (prev: Thread[]) => prev.filter(t => t.id !== threadId);
      setThreads(remove);
      setAllThreads(remove);
      setSearchResults(remove);
      setAgentFilterResults(remove);
      // Si el thread eliminado era el actual, limpiar selección
      setCurrentThread(prev => (prev && prev.id === threadId ? null : prev));
    } catch (error) {
      console.error('[useThreadManagement] Error al eliminar thread:', error);
    }
  }, []);

  // Aplicar un título a un thread en TODAS las cachés (issues #102, #501).
  // La lista visible puede venir de searchResults/agentFilterResults/allThreads
  // (ver getVisibleThreads), así que tocar solo `threads` deja el título viejo en
  // pantalla mientras haya búsqueda o filtro de agente activos hasta refrescar.
  //
  // `allThreads` NO es opcional, por dos motivos verificados con test:
  //   1. El backfill de servidor del filtro por agente construye su merge sobre el
  //      `clientResults` que capturó al arrancar. Si sigue en vuelo cuando llega el
  //      título, al resolver pisa el valor nuevo. Solo lo cancela (flag `cancelled`)
  //      que el efecto se vuelva a disparar, y su dependencia es `allThreads`.
  //   2. El filtro se re-deriva de `allThreads`, así que dejarla rancia hace que
  //      cualquier re-cálculo posterior revierta el título.
  // El efecto que sincroniza `threads → allThreads` es append-only (solo añade
  // hilos nuevos, nunca actualiza los existentes), así que tampoco lo propaga.
  //
  // Fuente única para renameThread (título escrito por el usuario) y para el
  // evento WebSocket `thread_title_updated` (título generado por LLM en backend).
  const applyThreadTitle = useCallback((threadId: string, newTitle: string) => {
    // Preserva la identidad del array cuando el hilo no está en esa caché: evita
    // re-renders y re-fetches espurios del efecto de filtro por agente.
    const apply = (prev: Thread[]) => {
      let changed = false;
      const next = prev.map(t => {
        if (t.id !== threadId) return t;
        changed = true;
        return { ...t, title: newTitle };
      });
      return changed ? next : prev;
    };
    setThreads(apply);
    setAllThreads(apply);
    setSearchResults(apply);
    setAgentFilterResults(apply);

    // Si el thread es el actual, actualizar también currentThread
    setCurrentThread(prev =>
      prev && prev.id === threadId ? { ...prev, title: newTitle } : prev
    );
  }, []);

  // Renombrar un thread
  const renameThread = useCallback(async (threadId: string, newTitle: string) => {
    try {
      const updatedThread = await threadService.updateThread(threadId, { title: newTitle });

      applyThreadTitle(threadId, updatedThread.title);

      console.log(`[useThreadManagement] Thread ${threadId} renombrado a "${newTitle}"`);
    } catch (error) {
      console.error('[useThreadManagement] Error al renombrar thread:', error);
      throw error;
    }
  }, [applyThreadTitle]);

  // Búsqueda híbrida: cliente primero, servidor si no hay resultados locales
  useEffect(() => {
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Si no hay término de búsqueda, restaurar threads originales
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    // Búsqueda del lado del cliente (instantánea)
    const searchTermLower = searchTerm.toLowerCase();
    const clientResults = allThreads.filter(thread =>
      thread.title.toLowerCase().includes(searchTermLower)
    );

    // Si hay resultados locales, mostrarlos inmediatamente
    if (clientResults.length > 0) {
      setSearchResults(clientResults);
      setIsSearching(false);
      return;
    }

    // Si no hay resultados locales, buscar en el servidor (con debounce)
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        console.log(`[useThreadManagement] Buscando en servidor: "${searchTerm}"`);
        const result = await threadService.searchThreads(searchTerm, {
          limit: 50,
          offset: 0
        });

        setSearchResults(result.threads);
        console.log(`[useThreadManagement] Resultados del servidor: ${result.threads.length}`);
      } catch (error) {
        console.error('[useThreadManagement] Error al buscar en servidor:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500); // Debounce de 500ms

    // Cleanup
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, allThreads]);

  // Actualizar allThreads cuando threads cambia (para búsqueda cliente)
  useEffect(() => {
    if (threads.length > 0) {
      setAllThreads(prevAll => {
        // Merge sin duplicados
        const merged = [...prevAll];
        threads.forEach(thread => {
          if (!merged.find(t => t.id === thread.id)) {
            merged.push(thread);
          }
        });
        return merged;
      });
    }
  }, [threads]);

  // setAgentFilter wrapper: clears search when filtering by agent
  const setAgentFilter = useCallback((rolId: string | null) => {
    setAgentFilterState(rolId);
    setSearchTerm('');
    if (!rolId) setAgentFilterResults([]);
  }, []);

  // Filtro híbrido por agente: cliente instantáneo + servidor para completitud
  useEffect(() => {
    if (!agentFilter) {
      setAgentFilterResults([]);
      return;
    }
    // Instant: filter from allThreads cache
    const clientResults = allThreads.filter(t => t.metadata?.rolId === agentFilter);
    setAgentFilterResults(clientResults);

    // Backfill: fetch from server to catch threads not in cache
    let cancelled = false;
    threadService.getThreads({ limit: 100, offset: 0, rolId: agentFilter })
      .then(result => {
        if (cancelled) return;
        // Merge server results with client results (dedup by id)
        const merged = [...clientResults];
        for (const t of result.threads) {
          if (!merged.find(m => m.id === t.id)) merged.push(t);
        }
        merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setAgentFilterResults(merged);
      })
      .catch(() => {}); // Client results are fine as fallback
    return () => { cancelled = true; };
  }, [agentFilter, allThreads]);

  // Determine visible threads: search > agent filter > all
  const getVisibleThreads = (): Thread[] => {
    if (searchTerm.trim()) return searchResults;
    if (agentFilter) return agentFilterResults;
    return threads;
  };

  return {
    threads: getVisibleThreads(),
    setThreads,
    currentThread,
    setCurrentThread,
    isThreadListOpen,
    setIsThreadListOpen,
    selectThread,
    clearTargetMessage,
    deleteThread,
    renameThread,
    applyThreadTitle,
    createThread,
    loadThreads,
    loadMoreThreads,
    hasMore: (searchTerm.trim() || agentFilter) ? false : hasMore, // Deshabilitar paginación en búsqueda/filtro
    isLoadingMore,
    isLoading,
    searchTerm,
    setSearchTerm,
    isSearching,
    searchResults,
    agentFilter,
    setAgentFilter,
  };
}; 